// desyr's admin service for desyr ID (94-services-api.md, desyr ID): called with the person's ID
// token; `desyr.id.sign_in` (401) renews the tokens and sends the call once more. Errors keep their
// `code` and status for the plain words of 02-building-blocks.md.
import type { DesyrIdConfig } from '../core/config';
import { idToken, IdError } from './oidc';

/** A circle of the person, as the admin service lists it (36, Behaviour 2.1; 94, `GET /me/circles`). */
export interface Circle {
  slug: string;
  name: string;
  pictureUrl: string | null;
  /** The circle's chat server name. */
  server: string;
  /** The person's chat ID there. */
  mxid: string;
  role: 'owner' | 'admin' | 'member';
}
/** An open invitation (37), listed only while the email is confirmed. */
export interface Invitation {
  id: string;
  circle: Pick<Circle, 'slug' | 'name' | 'pictureUrl'>;
  /** The person's name in that circle. */
  name: string;
  mxid: string;
  role: 'admin' | 'member';
  invitedBy: string;
  expiresAt: string;
}
export interface CirclesAnswer {
  emailVerified: boolean;
  /** The person's desyr ID has a passkey. */
  hasPasskey: boolean;
  circles: Circle[];
  invitations?: Invitation[];
}

/**
 * A call to desyr ID's admin routes (`desyrId.admin`, or a circle's `admin` address for its
 * invitation and member routes): JSON (or an image as itself) with the ID token, no cookies.
 */
export async function idCall<T>(config: DesyrIdConfig, path: string, init: { method?: string; body?: unknown; base?: string } = {}): Promise<T> {
  const base = new URL(init.base ?? config.admin, location.origin).href.replace(/\/+$/, '');
  const raw = init.body instanceof Blob ? init.body : undefined; // an image, sent as itself
  const send = async (force: boolean) => {
    const token = await idToken(config, force);
    if (!token) throw Object.assign(new IdError('failed'), { status: 401, code: 'desyr.id.sign_in' });
    return fetch(`${base}${path}`, {
      method: init.method ?? 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': raw ? raw.type : 'application/json' } : {}) },
      body: raw ?? (init.body ? JSON.stringify(init.body) : undefined),
    });
  };
  // desyr ID's token address not answering is desyr ID being down, as the service's 503: its own words
  // where the person started something, nothing else (36, Behaviour 1.4, v1.58.16).
  const down = (e: unknown) => {
    throw e instanceof IdError && e.reason === 'unavailable' ? Object.assign(new Error('desyr.id.unavailable'), { status: 503, code: 'desyr.id.unavailable' }) : e;
  };
  let answer = await send(false).catch(down);
  let body = await answer.json().catch(() => ({}));
  if (answer.status === 401 && body.code === 'desyr.id.sign_in') {
    answer = await send(true).catch(down);
    body = await answer.json().catch(() => ({}));
  }
  if (answer.ok) return (answer.status === 204 ? undefined : body) as T;
  throw Object.assign(new Error(body.code ?? `desyr id ${answer.status}`), { status: answer.status, code: body.code, retryAfter: answer.headers.get('Retry-After') ?? undefined });
}

/** The person's circles, with their invitations and whether their email is confirmed (`GET /me/circles`). */
export const myCircles = (config: DesyrIdConfig) => idCall<CirclesAnswer>(config, '/me/circles');

/** A circle's logo (`pictureUrl`, at the admin service), fetched with the ID token, as a local address. */
export async function circleLogo(config: DesyrIdConfig, pictureUrl: string): Promise<string | undefined> {
  const token = await idToken(config).catch(() => undefined);
  const url = new URL(pictureUrl, `${new URL(config.admin, location.origin).href.replace(/\/+$/, '')}/`);
  const answer = token && (await fetch(url, { credentials: 'omit', headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined));
  return answer && answer.ok ? URL.createObjectURL(await answer.blob()) : undefined;
}
